<script setup lang="ts">
import {
  CampaignSelector,
  type CampaignsFilter,
  CampaignStatusEnum,
  type ContentTypeOption,
  CreatorSelector,
  DateRangeSelector,
  type Segment,
  SegmentSelector,
  SortingComponent,
  SourceSelector,
  TagSelector,
} from '@metapic-apps/shared'
import { computed, ref, watch } from 'vue'

import { useGetCampaigns, useGetUserListRequest, useGetUserTagListRequest, useSearchSegments } from '@/api'
import AdvertiserGroupSelector from '@/components/Filter/AdvertiserGroupSelector.vue'
import AdvertiserSelector from '@/components/Filter/AdvertiserSelector.vue'
import ClientSelector from '@/components/Filter/ClientSelector.vue'
import MoreFiltersSelector from '@/components/Filter/MoreFiltersSelector.vue'
import QuerySelector from '@/components/Filter/QuerySelector.vue'
import StatusSelector from '@/components/Filter/StatusSelector.vue'
import type { FilterType, FilterValueType, ListItemsType } from '@/lib/types/common.types'
import { BoxComponent, ButtonComponent, FlexComponent } from '@/ui-library'
const props = withDefaults(
  defineProps<{
    placeholder?: string
    sortingItems?: ListItemsType
    moreFiltersItems?: ListItemsType
    statusOptions?: ListItemsType
    statusFieldKey?: string
    contentTypeOptions?: ContentTypeOption[]
    hasSeparateOrderSorting?: boolean
    isReturnAdEnabled?: boolean
    showSortingSelector?: boolean
    showAdvertiserSelector?: boolean
    showClientSelector?: boolean
    showStatusSelector?: boolean
    showMoreFiltersSelector?: boolean
    showCreatorSelector?: boolean
    showQuerySelector?: boolean
    showCampaignSelector?: boolean
    showDateRangeSelector?: boolean
    showSourceSelector?: boolean
    showSegmentSelector?: boolean
    showUserTagSelector?: boolean
    shouldTruncateText?: boolean
  }>(),
  {
    hasSeparateOrderSorting: true,
    statusFieldKey: 'status',
    showSortingSelector: true,
    isReturnAdEnabled: false,
    showAdvertiserSelector: false,
    showClientSelector: false,
    showStatusSelector: false,
    showMoreFiltersSelector: false,
    showCreatorSelector: false,
    showQuerySelector: true,
    showCampaignSelector: false,
    showDateRangeSelector: false,
    showSourceSelector: false,
    showSegmentSelector: false,
    showUserTagSelector: false,
    shouldTruncateText: false,
  },
)

const emit = defineEmits<{ clear: [] }>()

const filter = defineModel<
  FilterType & {
    contentTypes?: string[]
    segment?: Pick<Segment, 'id' | 'name'>
    userTags?: { id: number; name: string }[]
  }
>('modelValue', {
  required: true,
})

const dateRange = computed({
  get: () => ({ startDate: filter.value.dateFrom ?? '', endDate: filter.value.dateTo ?? '' }),
  set: (value) => {
    filter.value.dateFrom = value.startDate
    filter.value.dateTo = value.endDate
  },
})

const isClearFiltersButtonShown = computed(() => {
  if (!filter.value) {
    return false
  }

  return Object.entries(filter.value).some(([key, value]: [string, unknown]) => {
    if (['page', 'include', 'sortBy'].includes(key)) {
      return false
    }

    return Array.isArray(value) ? value.length > 0 : !!value
  })
})

const handleClear = () => emit('clear')

const campaignFilter = ref<CampaignsFilter>({
  page: 1,
  sortBy: 'created_at:desc',
  statuses: [CampaignStatusEnum.Active, CampaignStatusEnum.Scheduled],
  query: '',
})

watch(
  () => [filter.value.advertiserGroup, filter.value.advertiser] as const,
  ([advertiserGroup, advertiser]) => {
    campaignFilter.value.advertiserGroup = advertiserGroup ? { id: advertiserGroup.id, name: '' } : undefined
    campaignFilter.value.advertiser = advertiser ? { id: advertiser.id, label: '' } : undefined
  },
  { immediate: true },
)

const { data, isLoading: isCampaignsLoading } = useGetCampaigns(campaignFilter, {
  skip: !props.showCampaignSelector,
})
const userTagSearch = ref('')
const { data: userTagResults, isLoading: isUserTagsLoading } = useGetUserTagListRequest(userTagSearch, !props.showUserTagSelector)
const userTagOptions = computed(() => {
  const optionsById = new Map<number, { id: number; name: string }>()
  ;(filter.value.userTags ?? []).forEach((tag) => optionsById.set(tag.id, tag))
  userTagResults.value.forEach((tag) => optionsById.set(tag.id, { id: tag.id, name: tag.name }))
  return [...optionsById.values()]
})

const handleUserTagSelection = (ids: number[]) => {
  const namesById = new Map(userTagOptions.value.map((option) => [option.id, option.name]))
  filter.value.userTags = ids.map((id) => ({ id, name: namesById.get(id) ?? '' }))
}
</script>
<template>
  <BoxComponent>
    <FlexComponent wrap="wrap" :gap="4">
      <QuerySelector v-if="showQuerySelector && placeholder" v-model:query="filter.query" :placeholder="placeholder" />
      <AdvertiserGroupSelector
        v-model:advertiser-group="filter.advertiserGroup"
        :is-return-ad-enabled="isReturnAdEnabled"
        :should-truncate-text="shouldTruncateText"
      />
      <AdvertiserSelector
        v-if="showAdvertiserSelector"
        v-model:advertiser="filter.advertiser"
        :advertiser-group-id="filter.advertiserGroup?.id"
        :should-truncate-text="shouldTruncateText"
      />
      <CreatorSelector
        v-if="showCreatorSelector"
        v-model:creator="filter.creator"
        :advertiser-group-id="filter.advertiserGroup?.id"
        :advertiser-id="filter.advertiser?.id"
        :should-truncate-text="shouldTruncateText"
        :load-creators="useGetUserListRequest"
        :width="350"
      />
      <CampaignSelector
        v-if="showCampaignSelector"
        v-model:campaign="filter.campaign"
        :should-truncate-text="shouldTruncateText"
        :campaigns="data"
        :is-loading="isCampaignsLoading"
        :query="campaignFilter.query"
        name-key="internalName"
        @update:query="campaignFilter.query = $event"
      />
      <ClientSelector
        v-if="showClientSelector"
        v-model:client="filter.client"
        :advertiser-group-id="filter.advertiserGroup?.id"
        :should-truncate-text="shouldTruncateText"
      />
      <DateRangeSelector v-if="showDateRangeSelector" v-model:date-range="dateRange" />
      <StatusSelector
        v-if="showStatusSelector && statusOptions"
        :status="(filter as Record<string, FilterValueType>)[statusFieldKey]"
        :options="statusOptions"
        :should-truncate-text="shouldTruncateText"
        @update:status="(filter as Record<string, FilterValueType>)[statusFieldKey] = $event"
      />
      <TagSelector
        v-if="showUserTagSelector"
        :model-value="filter.userTags?.map(({ id }) => id) ?? []"
        :options="userTagOptions"
        :loading="isUserTagsLoading"
        server-search
        placeholder="Search user tags"
        all-label="User tags"
        @search="userTagSearch = $event"
        @update:model-value="handleUserTagSelection"
      />
      <SegmentSelector
        v-if="showSegmentSelector"
        v-model:segment="filter.segment"
        :advertiser-group-id="filter.advertiserGroup?.id"
        :load-segments="useSearchSegments"
        :should-truncate-text="shouldTruncateText"
      />
      <SourceSelector
        v-if="showSourceSelector && contentTypeOptions"
        v-model:content-types="filter.contentTypes"
        :content-type-options="contentTypeOptions"
      />
      <MoreFiltersSelector
        v-if="showMoreFiltersSelector && moreFiltersItems"
        v-model:statuses="filter.statuses"
        v-model:created-by-me="filter.createdByMe"
        :more-filters-items="moreFiltersItems"
      />
      <ButtonComponent v-if="isClearFiltersButtonShown" variant="text" class="filter--clear-filters" @click="handleClear">
        Clear filters
      </ButtonComponent>
      <SortingComponent
        v-if="showSortingSelector && sortingItems"
        v-model="filter.sortBy"
        :items="sortingItems"
        :has-separate-order-sorting="hasSeparateOrderSorting"
        class="filter--sort"
      />
    </FlexComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.filter--sort {
  margin-left: auto;

  @media screen and (max-width: 767px) {
    margin-left: initial;

    .flex {
      flex-direction: row-reverse;
    }
  }
}

.filter--clear-filters {
  padding-inline: 8px;
  transition: none;

  &:hover {
    border-radius: 8px;
    background: var(--button-hover);
  }
}

.button.filter--clear-filters {
  padding-left: 8px;
  padding-right: 8px;
}

:deep(.el-tooltip__trigger) {
  cursor: pointer;
  &:hover,
  &[aria-expanded='true'] {
    border-radius: 8px;
    background: var(--button-hover);
  }
  i {
    transition: all 0.3s ease-in;
  }

  &[aria-expanded='true'] {
    .chevron-icon {
      transform: rotate(180deg);
    }
  }
}
</style>
